import { useForm } from 'react-hook-form';
import { useEffect, useState, useRef, useCallback } from "react";
import Api from "../Auth/Api";
import { Toast, Tab, Tabs } from "react-bootstrap";
import { useAjustes } from "../Context/AjustesContext";
import ImageCardUpload from "../Components/Ajustes/ImageCardUpload";

const CAMPOS_ARQUIVO = ['logo', 'logo_white', 'favicon', 'flag'];

function ConfiguracoesGlobais() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [config, setConfig] = useState({});
    const [loading, setLoading] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [corPrimaria, setCorPrimaria] = useState("#3a5a6c");
    const [corSecundaria, setCorSecundaria] = useState("#6c8a9c");
    const [activeTab, setActiveTab] = useState("informacoes");
    const { recarregarAjustes } = useAjustes();

    const [fileSelected, setFileSelected] = useState({ logo: false, logo_white: false, favicon: false, flag: false });
    const fileInputRefs = {
        logo: useRef(null), logo_white: useRef(null), favicon: useRef(null), flag: useRef(null)
    };

    const { register, handleSubmit, setValue, control, formState: { errors } } = useForm();

    // Estilos de hover dos cards
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .ajustes-image-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
            .ajustes-image-card:hover { transform: translateY(-4px); box-shadow: 0 8px 16px rgba(0,0,0,0.12) !important; }
            .ajustes-upload-label { transition: all 0.2s ease; }
            .ajustes-upload-label:hover { transform: scale(1.01); box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
            .ajustes-icon-circle { transition: all 0.3s ease; }
            .ajustes-image-card:hover .ajustes-icon-circle { transform: scale(1.1) rotate(5deg); }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);

    const carregar = useCallback(async () => {
        try {
            setLoading(true);
            const response = await Api.get('api/configuracoes-globais', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = response.data;
            const comUrls = { ...data };
            CAMPOS_ARQUIVO.forEach(campo => {
                if (comUrls[campo]) comUrls[campo] = getImageUrl(comUrls[campo]);
            });
            setConfig(comUrls);
            setValue('titulo', data.titulo || '');
            setValue('cor_primaria', data.cor_primaria || "#3a5a6c");
            setValue('cor_secundaria', data.cor_secundaria || "#6c8a9c");
            setValue('email_contato', data.email_contato || '');
            setValue('whatsapp_contato', data.whatsapp_contato || '');
            setCorPrimaria(data.cor_primaria || "#3a5a6c");
            setCorSecundaria(data.cor_secundaria || "#6c8a9c");
        } catch (error) {
            console.error("Erro ao carregar configurações globais:", error);
        } finally {
            setLoading(false);
        }
    }, [token, getImageUrl, setValue]);

    useEffect(() => { carregar(); }, [carregar]);

    const onSubmit = async (data) => {
        if (!data.titulo) {
            setToast({ header: 'Erro!', body: 'O título da plataforma é obrigatório', type: 'toast-error' });
            setShowToast(true);
            return;
        }
        try {
            setSalvando(true);
            const formData = new FormData();
            formData.append('titulo', data.titulo);
            if (data.cor_primaria) formData.append('cor_primaria', data.cor_primaria);
            if (data.cor_secundaria) formData.append('cor_secundaria', data.cor_secundaria);
            if (data.email_contato) formData.append('email_contato', data.email_contato);
            if (data.whatsapp_contato) formData.append('whatsapp_contato', data.whatsapp_contato);
            CAMPOS_ARQUIVO.forEach(campo => {
                if (data[campo] && data[campo] instanceof FileList && data[campo].length > 0) {
                    formData.append(campo, data[campo][0]);
                }
            });

            await Api.post('api/configuracoes-globais', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            await recarregarAjustes();
            setFileSelected({ logo: false, logo_white: false, favicon: false, flag: false });
            await carregar();

            setToast({ header: 'Sucesso!', body: 'Configurações globais salvas! Recarregue as páginas para ver as alterações.', type: 'toast-success' });
            setShowToast(true);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao salvar configurações globais', type: 'toast-error' });
            setShowToast(true);
        } finally {
            setSalvando(false);
        }
    };

    const handleRemove = async (campo) => {
        if (!window.confirm(`Tem certeza que deseja remover este ${campo}?`)) return;
        try {
            const response = await Api.delete(`api/configuracoes-globais/arquivo/${campo}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFileSelected(prev => ({ ...prev, [campo]: false }));
            await carregar();
            await recarregarAjustes();
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            setShowToast(true);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || `Erro ao remover ${campo}`, type: 'toast-error' });
            setShowToast(true);
        }
    };

    const handleColorChange = (color, field) => {
        setValue(field, color);
        if (field === 'cor_primaria') setCorPrimaria(color);
        else if (field === 'cor_secundaria') setCorSecundaria(color);
    };

    const onSelect = (campo, selecionado) => setFileSelected(prev => ({ ...prev, [campo]: selecionado }));

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header><strong className="me-auto">{toast.header}</strong></Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mb-5">
                <div className="card shadow-sm border-0">
                    <div className="card-header bg-primary text-white py-3">
                        <h5 className="mb-0"><i className="bi bi-globe2 me-2"></i>Configurações Globais</h5>
                        <small className="text-white-50">Branding compartilhado por toda a plataforma — visível em todas as empresas</small>
                    </div>
                    <div className="card-body p-3 p-md-4">
                        {loading ? (
                            <div className="d-flex justify-content-center my-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit(onSubmit)}>
                                {/* Mobile: seletor de seção no lugar das abas */}
                                <div className="d-md-none mb-3">
                                    <label className="form-label text-primary fw-medium small mb-1">Seção</label>
                                    <select
                                        className="form-select"
                                        value={activeTab}
                                        onChange={(e) => setActiveTab(e.target.value)}
                                    >
                                        <option value="informacoes">Informações Gerais</option>
                                        <option value="cores">Cores</option>
                                        <option value="imagens">Imagens e Logos</option>
                                    </select>
                                </div>

                                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4 d-none d-md-flex">
                                    {/* Informações Gerais */}
                                    <Tab eventKey="informacoes" title={<><i className="bi bi-info-circle me-2"></i>Informações Gerais</>}>
                                        <div className="row mb-4 mt-4">
                                            <div className="col-md-12 mb-4">
                                                <label className="form-label fw-bold">Título da Plataforma</label>
                                                <input
                                                    type="text"
                                                    className={`form-control ${errors.titulo ? 'is-invalid' : ''}`}
                                                    {...register('titulo', { required: 'Título é obrigatório' })}
                                                />
                                                {errors.titulo && <div className="invalid-feedback">{errors.titulo.message}</div>}
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <label className="form-label fw-bold"><i className="bi bi-envelope me-2"></i>Email de Contato</label>
                                                <input type="email" className="form-control" {...register('email_contato')} placeholder="contato@email.com" />
                                                <small className="text-muted">Email usado para contatos gerais do sistema</small>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <label className="form-label fw-bold"><i className="bi bi-whatsapp me-2"></i>WhatsApp de Contato</label>
                                                <input maxLength="13" type="text" className="form-control" {...register('whatsapp_contato')} placeholder="5599999999999" />
                                                <small className="text-muted">Formato: 5599999999999 (código país + DDD + número)</small>
                                            </div>
                                        </div>
                                    </Tab>

                                    {/* Cores */}
                                    <Tab eventKey="cores" title={<><i className="bi bi-palette me-2"></i>Cores</>}>
                                        <div className="row mb-4 mt-4">
                                            <div className="col-md-6 mb-4">
                                                <label className="form-label fw-bold">Cor Primária</label>
                                                <div className="input-group">
                                                    <input type="color" className="form-control form-control-color" value={corPrimaria} onChange={(e) => handleColorChange(e.target.value, 'cor_primaria')} />
                                                    <input type="text" className="form-control" value={corPrimaria} onChange={(e) => handleColorChange(e.target.value, 'cor_primaria')} />
                                                </div>
                                            </div>
                                            <div className="col-md-6 mb-4">
                                                <label className="form-label fw-bold">Cor Secundária</label>
                                                <div className="input-group">
                                                    <input type="color" className="form-control form-control-color" value={corSecundaria} onChange={(e) => handleColorChange(e.target.value, 'cor_secundaria')} />
                                                    <input type="text" className="form-control" value={corSecundaria} onChange={(e) => handleColorChange(e.target.value, 'cor_secundaria')} />
                                                </div>
                                            </div>
                                        </div>
                                    </Tab>

                                    {/* Imagens e Logos */}
                                    <Tab eventKey="imagens" title={<><i className="bi bi-image me-2"></i>Imagens e Logos</>}>
                                        <div className="row mb-4 mt-4">
                                            <div className="col-md-12 mb-4">
                                                <h6 className="text-muted mb-0 fw-normal" style={{ fontSize: '0.85rem', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                                    Imagens e Logos
                                                </h6>
                                            </div>
                                            <ImageCardUpload campo="logo" label="Logo Principal" icon="bi-image" iconBg="bg-primary bg-opacity-10" iconColor="text-primary"
                                                valor={config.logo} selecionado={fileSelected.logo} inputRef={fileInputRefs.logo} control={control} onSelect={onSelect} onRemove={handleRemove} />
                                            <ImageCardUpload campo="logo_white" label="Logo Branco" icon="bi-image" iconBg="bg-dark bg-opacity-10" iconColor="text-dark"
                                                valor={config.logo_white} selecionado={fileSelected.logo_white} inputRef={fileInputRefs.logo_white} control={control} onSelect={onSelect} onRemove={handleRemove} previewDark />
                                            <ImageCardUpload campo="favicon" label="Favicon" icon="bi-bookmark" iconBg="bg-warning bg-opacity-10" iconColor="text-warning"
                                                valor={config.favicon} selecionado={fileSelected.favicon} inputRef={fileInputRefs.favicon} control={control} onSelect={onSelect} onRemove={handleRemove} />
                                            <ImageCardUpload campo="flag" label="Flag" icon="bi-flag" iconBg="bg-info bg-opacity-10" iconColor="text-info"
                                                valor={config.flag} selecionado={fileSelected.flag} inputRef={fileInputRefs.flag} control={control} onSelect={onSelect} onRemove={handleRemove} />
                                        </div>
                                    </Tab>
                                </Tabs>

                                <div className="d-grid d-md-flex justify-content-md-end mt-4">
                                    <button type="submit" className="btn btn-primary" disabled={salvando}>
                                        {salvando ? (
                                            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Salvando...</>
                                        ) : (
                                            <><i className="bi bi-save me-2"></i>Salvar Configurações</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ConfiguracoesGlobais;
